import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { FaqSpace } from './types';
import { buildFaqAdminPath, buildFaqPath } from './faq-space-utils';

type FaqSpaceContextValue = {
  space: FaqSpace;
  basePath: string;
  adminBasePath: string;
  path: (path?: string) => string;
  adminPath: (path?: string) => string;
};

const FaqSpaceContext = createContext<FaqSpaceContextValue | null>(null);

export function FaqSpaceProvider({
  space,
  children,
}: {
  space: FaqSpace;
  children: ReactNode;
}) {
  const value = useMemo<FaqSpaceContextValue>(() => ({
    space,
    basePath: buildFaqPath(space),
    adminBasePath: buildFaqAdminPath(space),
    path: (path?: string) => buildFaqPath(space, path),
    adminPath: (path?: string) => buildFaqAdminPath(space, path),
  }), [space]);

  return (
    <FaqSpaceContext.Provider value={value}>
      {children}
    </FaqSpaceContext.Provider>
  );
}

export function useFaqSpace() {
  const value = useContext(FaqSpaceContext);
  if (!value) {
    throw new Error('useFaqSpace must be used inside FaqSpaceProvider');
  }
  return value;
}

export function useOptionalFaqSpace() {
  return useContext(FaqSpaceContext);
}
